import React, { useEffect } from 'react'

import { DDSTypography } from 'den-design-system'
import { Grid } from '@mui/material'
import SFPermissionSetLicenses from './SFPermissionSetLicenses'
import SFUserLicensesTab from './SFUserLicensesTab'
import Tabs from '../../components/common/DDS/Tabs/Tabs'
import ToastNotification from '../../components/common/DDS/Toast/Toast'
import { convertJsonToCsvDownload } from '../../utils/FileUtils'
import { observer } from 'mobx-react-lite'
import pallete from '../../global/newPallete'
import { replaceUrl } from '../../utils/UrlUtils'
import routeConstants from '../../constants/RouteConstants'
import { timeFormats } from '../../enum/TIME'
import { timeZone } from '../../utils/DateTimeUtils'
import { useSearchParams } from 'react-router-dom'
import { useStoreContext } from '../../store/StoreContext'

const SFLicensesOverview: React.FC = () => {
  const store = useStoreContext()
  const { salesforceAuditStore } = useStoreContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const [tabIndex, setTabIndex] = React.useState<string>(
    searchParams.get('tab') ?? '1'
  )

  const downloadLicensesReport = async () => {
    store.uiStore.setGlobalLoader(true)

    try {
      const orgTZ = store.userStore.getOrganizationPreference().timeZone

      const data =
        tabIndex === '1'
          ? salesforceAuditStore.getLicenses() // User Licenses
          : salesforceAuditStore.getPermissionSetsLicenses() // Permission Set Licenses

      if (!data || data.length === 0) {
        ToastNotification({
          type: 'info',
          message: 'No data available to export'
        })
        return
      }

      const mappedData = data.map((row: any) => {
        const used = Number(row?.UsedLicenses || 0)
        const total = Number(row?.TotalLicenses || 0)

        return {
          MasterLabel: row?.MasterLabel || row?.Name || '-',
          Used: used.toLocaleString() || '0',
          Total: total.toLocaleString() || '0',
          Status: row?.Status || '-',
          LastModifiedDate:
            row?.LastModifiedDate && row?.LastModifiedDate !== '-'
              ? timeZone(
                  orgTZ,
                  row.LastModifiedDate,
                  timeFormats.fullMonthDateYearTime
                )
              : '-'
        }
      })

      const date = new Date()
      const formattedDate = `${(date.getMonth() + 1)
        .toString()
        .padStart(
          2,
          '0'
        )}-${date.getDate().toString().padStart(2, '0')}-${date.getFullYear()}`

      const reportName =
        tabIndex === '1'
          ? 'Salesforce_UserLicenses_Report'
          : 'Salesforce_PermissionSetLicenses_Report'

      convertJsonToCsvDownload(mappedData, `${reportName}_${formattedDate}.csv`)

      ToastNotification({
        type: 'success',
        message: `Successfully exported ${mappedData.length} ${
          tabIndex === '1' ? 'User License(s)' : 'Permission Set License(s)'
        }`
      })
    } catch (error: any) {
      ToastNotification({
        type: 'error',
        message:
          typeof error === 'string'
            ? error
            : error?.message || 'Error downloading licenses report'
      })
    } finally {
      store.uiStore.setGlobalLoader(false)
    }
  }

  const tabs = [
    {
      id: '1',
      label:
        tabIndex === '1' ? (
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.primary2}
          >
            User Licenses
          </DDSTypography.Title>
        ) : (
          <DDSTypography.Title
            type='h5'
            variant='medium'
            color={pallete.colors.textDark5}
          >
            User Licenses
          </DDSTypography.Title>
        ),
      children: (
        <div>
          <SFUserLicensesTab />
        </div>
      )
    },
    {
      id: '2',
      label:
        tabIndex === '2' ? (
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.primary2}
          >
            Permission Set Licenses
          </DDSTypography.Title>
        ) : (
          <DDSTypography.Title
            type='h5'
            variant='medium'
            color={pallete.colors.textDark5}
          >
            Permission Set Licenses
          </DDSTypography.Title>
        ),
      children: (
        <Grid item xs={12}>
          <SFPermissionSetLicenses />
        </Grid>
      )
    }
  ]

  useEffect(() => {
    const organizationId = store.scopeStore.getScope()[
      'organization-id'
    ] as string
    const projectId = store.scopeStore.getScope()['project-id'] as string
    const environmentId = store.scopeStore.getScope()[
      'environment-id'
    ] as string

    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: 'Salesforce Audit',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_DASHBOARD,
          organizationId,
          projectId,
          environmentId
        )
      },
      {
        label: (
          <div
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
            className='cursor-not-allowed'
          >
            <div
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              className='pointer-events-none'
            >
              <DDSTypography.Paragraph
                size='para'
                variant='medium'
                color={pallete.colors.textDark4}
              >
                Licenses
              </DDSTypography.Paragraph>
            </div>
          </div>
        ),
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_LICENSES_OVERVIEW,
          organizationId,
          projectId,
          environmentId
        )
      },
      {
        label: tabIndex === '1' ? 'User Licenses' : 'Permission Set Licenses',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_LICENSES_OVERVIEW,
          organizationId,
          projectId,
          environmentId
        )
      }
    ])
  }, [tabIndex])

  return (
    <div className='flex flex-col w-full py-[24px] '>
      <Tabs
        tabOptionsStyle={{
          minWidth: 'fit-content',
          width: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '8px 12px',
          marginRight: '8px'
        }}
        tabOptions={tabs}
        id='tabs-grouped-api'
        onChange={(index: any) => {
          setTabIndex(index.toString())
          setSearchParams({
            ...searchParams,
            tab: index.toString()
          })
        }}
        exportOption={true}
        actionButtonDisabled={false}
        showActionButton={true}
        actionButtonLabel='Export'
        onActionButtonPress={downloadLicensesReport}
        activeTab={tabIndex}
      />
    </div>
  )
}

export default observer(SFLicensesOverview)
